import React from 'react'
import IPRToothIcon from './IPRToothIcon'

export interface IPRData {
  from: number
  to: number
  value: number
}

interface IPRToothChartProps {
  data?: IPRData[]
  editable?: boolean
}

const mockIPRData: IPRData[] = [
  { from: 7, to: 8, value: 0.2 },
  { from: 8, to: 9, value: 0.3 },
  { from: 10, to: 11, value: 0.2 },
  { from: 24, to: 25, value: 0.4 },
  { from: 26, to: 27, value: 0.3 },
  { from: 30, to: 31, value: 0.2 },
]

const IPRToothChart: React.FC<IPRToothChartProps> = ({
  data = mockIPRData,
}) => {
  const allIPRTeeth = new Set<number>()
  data.forEach(({ from, to }) => {
    allIPRTeeth.add(from)
    allIPRTeeth.add(to)
  })

  const toothWidth = 40
  const gap = 16
  const totalSlotWidth = toothWidth + gap

  const renderToothRow = (
    start: number,
    end: number,
    isLower = false,
  ): JSX.Element => {
    const teeth = []
    const direction = start < end ? 1 : -1

    for (let i = start; i !== end + direction; i += direction) {
      const active = allIPRTeeth.has(i)

      teeth.push(
        <div
          key={i}
          style={{
            position: 'relative',
            minWidth: toothWidth,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'space-between',
            height: 120,
          }}
        >
          {!isLower && (
            <div style={{ fontSize: '0.75rem', fontWeight: 'bold' }}>{i}</div>
          )}

          {isLower && <div style={{ height: 12 }} />}

          <IPRToothIcon id={i} active={active} />

          {!isLower && <div style={{ height: 12 }} />}

          {isLower && (
            <div style={{ fontSize: '0.75rem', fontWeight: 'bold' }}>{i}</div>
          )}
        </div>,
      )
    }

    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          flexDirection: isLower ? 'row-reverse' : 'row',
          gap: gap,
          marginTop: isLower ? 8 : 0,
          marginBottom: isLower ? 0 : 8,
        }}
      >
        {teeth}
      </div>
    )
  }

  const renderIPRValues = (): JSX.Element[] => {
    return data.map(({ from, to, value }, idx) => {
      const isLower = Math.min(from, to) >= 17
      const fromIndex = isLower ? 32 - from : from - 1
      const toIndex = isLower ? 32 - to : to - 1
      const avgIndex = (fromIndex + toIndex) / 2

      return (
        <div
          key={idx}
          style={{
            position: 'absolute',
            left: `${(avgIndex + 1) * totalSlotWidth - totalSlotWidth / 2}px`,
            top: isLower ? 210 : 0,
            height: 40,
            width: 2,
            backgroundColor: '#F59E0B',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            zIndex: 5,
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: isLower ? 40 : -20,
              fontSize: '0.75rem',
              background: '#FEF3C7',
              padding: '2px 3px',
              borderRadius: 4,
              border: '1px solid #F59E0B',
              transform: 'translateX(-50%)',
              whiteSpace: 'nowrap',
            }}
          >
            {value}
          </div>
        </div>
      )
    })
  }

  return (
    <div className="mt-2 mb-4">
      <div
        style={{
          position: 'relative',
          fontSize: '0.7rem',
          backgroundColor: '#FDE68A',
          padding: '2px 4px',
          borderRadius: 4,
          zIndex: 10,
          width: '102px',
          marginBottom: 8,
        }}
      >
        Values in mm
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
        <div style={{ position: 'relative', maxWidth: totalSlotWidth * 16 }}>
          {renderIPRValues()}
          {renderToothRow(1, 16)}

          <div
            style={{
              borderTop: '1px solid #D1D5DB',
              margin: 'auto',
              width: '100%',
            }}
          />

          {renderToothRow(17, 32, true)}
        </div>
      </div>
    </div>
  )
}

export default IPRToothChart
